import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Reveal, Chapter } from "./Reveal";
import SimPackageBuilder from "./sim/SimPackageBuilder";
import SimPricingPublish from "./sim/SimPricingPublish";
import SimInbox from "./sim/SimInbox";
import SimQuote from "./sim/SimQuote";
import SimWebsiteBuilder from "./sim/SimWebsiteBuilder";

const ChartMock = () => (
  <div className="rounded-xl border border-sand bg-white p-4">
    <div className="mb-3 flex items-center justify-between">
      <p className="text-xs font-bold text-ink">Business overview · enquiries</p>
      <span className="rounded-full bg-gold/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-gold">Sample dashboard data</span>
    </div>
    <div className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={[{m:"Jan",v:30},{m:"Feb",v:44},{m:"Mar",v:39},{m:"Apr",v:58},{m:"May",v:52},{m:"Jun",v:71},{m:"Jul",v:84}]} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <defs><linearGradient id="fx" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C49A5A" stopOpacity={0.35} /><stop offset="100%" stopColor="#C49A5A" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid stroke="#EFECE6" vertical={false} />
          <XAxis dataKey="m" tick={{ fontSize: 9, fill: "#57616F" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: "#57616F" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E6DFD0", fontSize: 11 }} />
          <Area type="monotone" dataKey="v" stroke="#C49A5A" strokeWidth={2.5} fill="url(#fx)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      {["Top destinations", "Top packages", "Conversion"].map((m) => (
        <div key={m} className="rounded-lg bg-paper px-3 py-2 text-center font-mono text-[9px] uppercase tracking-wider text-stone2">{m}</div>
      ))}
    </div>
  </div>
);

const ListMock = ({ rows }) => (
  <div className="space-y-2 rounded-xl border border-sand bg-white p-4">
    {rows.map(([a, b]) => (
      <div key={a} className="flex items-center justify-between rounded-lg bg-paper px-3.5 py-2.5">
        <span className="text-xs font-semibold text-ink">{a}</span>
        <span className="font-mono text-[10px] text-stone2">{b}</span>
      </div>
    ))}
  </div>
);

const TABS = [
  { label: "Dashboard", headline: "Your business at a glance", desc: "Enquiries, quotes, top destinations and conversion — one morning view.", benefits: ["Business overview", "Open enquiries", "Performance trends"], workflow: "Log in → See today → Act", visual: <ChartMock /> },
  { label: "Destination Manager", headline: "Your catalogue, structured", desc: "Destinations, hotels, activities and transfers with content, media and rates.", benefits: ["Rich product records", "Seasonal rates & validity", "Media library built in"], workflow: "Add product → Enrich → Publish", visual: <ListMock rows={[["Maldives", "12 resorts · 4 packages"], ["Velassaru Maldives", "★★★★★ · 3 seasons"], ["Sunset Cruise", "Private · $120"], ["Airport → Resort", "Speedboat · Pvt"]]} /> },
  { label: "Package Builder", headline: "Complete trips in minutes", desc: "Drag days into place from your own inventory — then price and publish.", benefits: ["Drag-and-drop days", "Reusable components", "Live itinerary preview"], workflow: "Build → Price → Publish", visual: <SimPackageBuilder /> },
  { label: "Pricing", headline: "Price it your way", desc: "Supplier costs plus your markup — displayed in the currencies you sell in.", benefits: ["Fixed or % markup", "Multi-currency display", "Seasonal & occupancy rates"], workflow: "Cost → Markup → Selling price", visual: <SimPricingPublish /> },
  { label: "Inquiry Workspace", headline: "Every enquiry, one workspace", desc: "Statuses, assignment, conversation and trip details per request.", benefits: ["New → Confirmed statuses", "Assign to your team", "Full context per inquiry"], workflow: "Receive → Assign → Respond", visual: <SimInbox /> },
  { label: "Quotation", headline: "Proposals, not messages", desc: "Itemized branded quotes your customers can review and respond to.", benefits: ["Line-item pricing", "Customer preview", "Accept / request changes"], workflow: "Build → Preview → Send", visual: <SimQuote /> },
  { label: "Website Builder", headline: "Your brand, your storefront", desc: "Name, colours, hero imagery and domain — your branded destination storefront.", benefits: ["White-label everything", "Custom domain", "SEO-ready pages"], workflow: "Brand → Sections → Publish", visual: <SimWebsiteBuilder /> },
];

export default function FeatureExplorer() {
  const [tab, setTab] = useState(0);
  const t = TABS[tab];

  return (
    <section id="features" className="border-y border-sand bg-parch py-24 lg:py-32">
      <div className="container-x">
        <Reveal><Chapter index={7} label="Product Walkthrough" /></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-8 max-w-2xl font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Walk through the actual product.
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-stone2">
            Seven workspaces, all connected. Everything below is playable with
            labeled sample data.
          </p>
        </Reveal>
        <div className="mt-10 flex gap-2 overflow-x-auto pb-2" data-lenis-prevent data-testid="feature-tabs">
          {TABS.map((tb, i) => (
            <button
              key={tb.label}
              onClick={() => setTab(i)}
              data-testid={`feature-tab-${tb.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                tab === i ? "bg-ink text-paper" : "border border-sand bg-white text-stone2 hover:text-ink"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 grid grid-cols-1 items-start gap-8 lg:grid-cols-2"
            data-testid="feature-panel"
          >
            <div>
              <h3 className="font-serif text-2xl text-ink sm:text-3xl" data-testid="feature-headline">{t.headline}</h3>
              <p className="mt-3 max-w-md text-base leading-relaxed text-stone2">{t.desc}</p>
              <ul className="mt-6 space-y-2.5">
                {t.benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-ink">
                    <span className="h-1.5 w-1.5 rounded-full bg-terra" /> {b}
                  </li>
                ))}
              </ul>
              <p className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-stone2">
                {t.workflow}
              </p>
            </div>
            <div>{t.visual}</div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
